import { ReactNode, createContext, useContext, useMemo, useCallback } from 'react';
// hooks
import useLocalStorage from '../../../hooks/useLocalStorage';
import MapRepository from '../map/repository';
import { usePostHogEvents } from '../cookies/usePostHogEvents';
import { EVENT_HAPTIC_FEEDBACK_TOGGLED } from 'common/infrastructure/analytics/events';

// ----------------------------------------------------------------------

export type ColorBlindModeValue =
  | 'default'
  | 'protanopia'
  | 'deuteranopia'
  | 'tritanopia'
  | 'achromatopsia';

export type SettingsValueProps = {
  colorBlindMode: ColorBlindModeValue;
  hapticFeedbackEnabled: boolean;
};

export type SettingsContextProps = SettingsValueProps & {
  // Color Blind Mode
  onChangeColorBlindMode: (event: React.ChangeEvent<HTMLInputElement>) => void;
  // Haptic Feedback
  onToggleHapticFeedback: (checked: boolean) => void;
};

// ----------------------------------------------------------------------

export const defaultSettings: SettingsValueProps = {
  colorBlindMode: 'default',
  hapticFeedbackEnabled: false,
};

const initialState: SettingsContextProps = {
  ...defaultSettings,
  // Color Blind Mode
  onChangeColorBlindMode: () => {},
  // Haptic Feedback
  onToggleHapticFeedback: () => {},
};

// ----------------------------------------------------------------------

const SettingsContext = createContext(initialState);

export const useSettingsContext = () => {
  const context = useContext(SettingsContext);

  if (!context) throw new Error('useSettingsContext must be use inside SettingsProvider');

  return context;
};

// ----------------------------------------------------------------------

type SettingsProviderProps = {
  children: ReactNode;
};

export function SettingsProvider({ children }: SettingsProviderProps) {
  const [settings, setSettings] = useLocalStorage('settings', defaultSettings);
  const { trackEvent } = usePostHogEvents();

  // Color Blind Mode
  const onChangeColorBlindMode = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const colorBlindMode = event.target.value;
      setSettings((prevSettings: SettingsValueProps) => ({ ...prevSettings, colorBlindMode })); // ✅ Uses previous state
      MapRepository.clearAssignments();
    },
    [setSettings] // ✅ Only stable references
  );

  // Haptic Feedback
  const onToggleHapticFeedback = useCallback(
    (checked: boolean) => {
      setSettings((prevSettings: SettingsValueProps) => ({
        ...prevSettings,
        hapticFeedbackEnabled: checked,
      }));

      trackEvent(EVENT_HAPTIC_FEEDBACK_TOGGLED, {
        enabled: checked,
      });
    },
    [setSettings, trackEvent]
  );

  const value = useMemo(
    () => ({
      ...settings,
      // Color Blind Mode
      onChangeColorBlindMode,
      // Haptic Feedback
      onToggleHapticFeedback,
    }),
    [settings, onChangeColorBlindMode, onToggleHapticFeedback]
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}
